import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Stack,
  CircularProgress,
} from '@mui/material';
import { ArrowBack, Save, Work } from '@mui/icons-material';
import PITargetRangeSelector from '../components/PITargetRangeSelector';
import PIJobReportHelper from '../components/PIJobReportHelper';
import { jobsApi } from '../services/api';
import { Job } from '../types';

const EditJob: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    title: '',
    department: '',
    description: '',
    location: '',
    employment_type: 'full_time' as 'full_time' | 'part_time' | 'contract' | 'temporary',
    salary_min: '',
    salary_max: '',
    positions_available: '1',
    application_deadline: '',
    status: 'open' as 'open' | 'paused' | 'closed' | 'filled',
    owner_id: '',
    // PI Target Ranges
    ideal_factor_a_min: 42,
    ideal_factor_a_max: 58,
    ideal_factor_b_min: 42,
    ideal_factor_b_max: 58,
    ideal_factor_c_min: 42,
    ideal_factor_c_max: 58,
    ideal_factor_d_min: 42,
    ideal_factor_d_max: 58,
    requires_a_greater_than_b: false,
    requires_a_greater_than_c: false,
    requires_a_greater_than_d: false,
    requires_b_greater_than_a: false,
    requires_b_greater_than_c: false,
    requires_b_greater_than_d: false,
    requires_c_greater_than_a: false,
    requires_c_greater_than_b: false,
    requires_c_greater_than_d: false,
    requires_d_greater_than_a: false,
    requires_d_greater_than_b: false,
    requires_d_greater_than_c: false,
    job_summary: '',
    behavioral_description: '',
  });
  
  const [users, setUsers] = useState<Array<{id: number, name: string, role: string}>>([]);

  useEffect(() => {
    fetchJob();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/users/active', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const fetchJob = async () => {
    try {
      setLoading(true);
      const response = await jobsApi.getById(id!);
      const job = response.data;
      
      // Format date for HTML date input
      const formattedDeadline = job.application_deadline 
        ? new Date(job.application_deadline).toISOString().split('T')[0]
        : '';

      setFormData({
        title: job.title || '',
        department: job.department || '',
        description: job.job_summary || job.description || '',
        location: job.location || '',
        owner_id: job.owner_id?.toString() || '',
        employment_type: job.employment_type || 'full_time',
        salary_min: job.salary_min?.toString() || '',
        salary_max: job.salary_max?.toString() || '',
        positions_available: job.positions_available?.toString() || '1',
        application_deadline: formattedDeadline,
        status: job.status || 'open',
        // PI Target Ranges
        ideal_factor_a_min: job.ideal_factor_a_min ?? 42,
        ideal_factor_a_max: job.ideal_factor_a_max ?? 58,
        ideal_factor_b_min: job.ideal_factor_b_min ?? 42,
        ideal_factor_b_max: job.ideal_factor_b_max ?? 58,
        ideal_factor_c_min: job.ideal_factor_c_min ?? 42,
        ideal_factor_c_max: job.ideal_factor_c_max ?? 58,
        ideal_factor_d_min: job.ideal_factor_d_min ?? 42,
        ideal_factor_d_max: job.ideal_factor_d_max ?? 58,
        requires_a_greater_than_b: job.requires_a_greater_than_b || false,
        requires_a_greater_than_c: job.requires_a_greater_than_c || false,
        requires_a_greater_than_d: job.requires_a_greater_than_d || false,
        requires_b_greater_than_a: job.requires_b_greater_than_a || false,
        requires_b_greater_than_c: job.requires_b_greater_than_c || false,
        requires_b_greater_than_d: job.requires_b_greater_than_d || false,
        requires_c_greater_than_a: job.requires_c_greater_than_a || false,
        requires_c_greater_than_b: job.requires_c_greater_than_b || false,
        requires_c_greater_than_d: job.requires_c_greater_than_d || false,
        requires_d_greater_than_a: job.requires_d_greater_than_a || false,
        requires_d_greater_than_b: job.requires_d_greater_than_b || false,
        requires_d_greater_than_c: job.requires_d_greater_than_c || false,
        job_summary: job.job_summary || '',
        behavioral_description: job.behavioral_description || '',
      });
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to load job');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      const { owner_id, ...restFormData } = formData;
      const updateData: Partial<Job> = {
        ...restFormData,
        salary_min: formData.salary_min ? parseInt(formData.salary_min) : undefined,
        salary_max: formData.salary_max ? parseInt(formData.salary_max) : undefined,
        positions_available: parseInt(formData.positions_available),
        application_deadline: formData.application_deadline || undefined,
        job_summary: formData.description, // Map description field to job_summary
        owner_id: owner_id ? parseInt(owner_id) : undefined,
      };

      await jobsApi.update(id!, updateData);
      setSuccess('Job updated successfully!');
      
      // Redirect to job detail page after 2 seconds
      setTimeout(() => {
        navigate(`/jobs/${id}`);
      }, 2000);
      
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to update job');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(`/jobs/${id}`)}
        sx={{ mb: 2 }}
      >
        Back to Job
      </Button>

      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center">
            <Work sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4">Edit Job</Typography>
          </Box>
          
          {/* Action buttons at the top for easy access */}
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              onClick={() => navigate(`/jobs/${id}`)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={submitting ? <CircularProgress size={20} /> : <Save />}
              disabled={submitting}
              onClick={handleSubmit}
              data-testid="save-job-button"
            >
              {submitting ? 'Updating...' : 'Update Job'}
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* Basic Information */}
            <Typography variant="h6">Job Details</Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 2 }}>
              <TextField
                fullWidth
                label="Job Title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                required
              />
              
              <TextField
                fullWidth
                label="Department"
                value={formData.department}
                onChange={(e) => handleInputChange('department', e.target.value)}
                required
              />
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr' }, gap: 2 }}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
              />

              <FormControl fullWidth>
                <InputLabel>Employment Type</InputLabel>
                <Select
                  value={formData.employment_type}
                  onChange={(e) => handleInputChange('employment_type', e.target.value)}
                  label="Employment Type"
                >
                  <MenuItem value="full_time">Full Time</MenuItem>
                  <MenuItem value="part_time">Part Time</MenuItem>
                  <MenuItem value="contract">Contract</MenuItem>
                  <MenuItem value="temporary">Temporary</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Positions Available"
                type="number"
                inputProps={{ min: 1 }}
                value={formData.positions_available}
                onChange={(e) => handleInputChange('positions_available', e.target.value)}
                required
              />
            </Box>

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                label="Status"
              >
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="paused">Paused</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
                <MenuItem value="filled">Filled</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl fullWidth>
              <InputLabel>Job Owner</InputLabel>
              <Select
                value={formData.owner_id}
                onChange={(e) => handleInputChange('owner_id', e.target.value)}
                label="Job Owner"
              >
                <MenuItem value="">Select Owner</MenuItem>
                {users.map(user => (
                  <MenuItem key={user.id} value={user.id.toString()}>
                    {user.name} ({user.role})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Job Summary"
              multiline
              rows={8}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Describe the role, responsibilities, requirements, and what the candidate will be doing..."
              helperText="Provide a comprehensive summary of the position"
            />


            {/* Salary and Deadline */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2 }}>
              <TextField
                fullWidth
                label="Minimum Salary"
                type="number"
                value={formData.salary_min}
                onChange={(e) => handleInputChange('salary_min', e.target.value)}
                placeholder="e.g. 50000"
              />

              <TextField
                fullWidth
                label="Maximum Salary"
                type="number"
                value={formData.salary_max}
                onChange={(e) => handleInputChange('salary_max', e.target.value)}
                placeholder="e.g. 70000"
              />

              <TextField
                fullWidth
                label="Application Deadline"
                type="date"
                value={formData.application_deadline}
                onChange={(e) => handleInputChange('application_deadline', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            {/* PI Job Report Helper */}
            <PIJobReportHelper 
              onExtract={(data) => {
                // Fill job title if empty
                if (data.jobTitle && !formData.title) {
                  handleInputChange('title', data.jobTitle);
                }
                // Fill department if empty
                if (data.department && !formData.department) {
                  handleInputChange('department', data.department);
                }
                // Fill job summary
                if (data.jobSummary) {
                  handleInputChange('job_summary', data.jobSummary);
                }
                // Fill job characteristics as part of description
                if (data.jobCharacteristics && !formData.description) {
                  handleInputChange('description', data.jobCharacteristics);
                }
                // Fill behavioral description
                if (data.behaviors && data.behaviors.length > 0) {
                  handleInputChange('behavioral_description', data.behaviors.join(', '));
                }
                // Apply suggested PI ranges
                if (data.suggestedRanges) {
                  Object.entries(data.suggestedRanges).forEach(([key, value]) => {
                    handleInputChange(key, value);
                  });
                }
              }}
            />

            {/* PI Behavioral Target */}
            <PITargetRangeSelector 
              values={formData}
              onChange={handleInputChange}
            />

            {/* Submit Button */}
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                onClick={() => navigate(`/jobs/${id}`)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={submitting ? <CircularProgress size={20} /> : <Save />}
                disabled={submitting}
                data-testid="save-job-button"
              >
                {submitting ? 'Updating...' : 'Update Job'}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  );
};

export default EditJob;